import { computed } from 'vue'
import pluralize from 'pluralize'

import {
  BillingAlertType,
  PlanType,
  UserPermission,
} from '@/shared/graphql/legacy/generated'
import { useBillingStore } from '@/features/billing/store'
import { useUserStore } from '@/shared/stores/user'
import { formatNumber } from '@/shared/utils/number'
import { formatPrice } from '@/shared/utils/money'

type MenuTier = 'basic' | 'bundle' | 'pro'

export type MenuBanner = {
  variant: 'info' | 'error'
  text: string
}

export type MenuCta = {
  label: string
  action: 'upgradePro' | 'upgradeBundle'
}

const FAILURE_ALERTS = [
  BillingAlertType.Overdue,
  BillingAlertType.RequiresAction,
  BillingAlertType.Chargeback,
]

export const useMenuModel = () => {
  const userStore = useUserStore()
  const billingStore = useBillingStore()

  const user = computed(() => userStore.user)
  const billing = computed(() => billingStore.billing)

  const isPaid = computed(
    () => !!user.value?.permissions.includes(UserPermission.Pro)
  )

  const tier = computed<MenuTier>(() => {
    if (!isPaid.value) return 'basic'
    return billing.value?.plan.type === PlanType.Pro ? 'pro' : 'bundle'
  })

  const used = computed(() => user.value?.totalFiles ?? 0)

  const limit = computed(() => {
    if (tier.value === 'pro') return null
    return typeof user.value?.totalFilesLimit === 'number'
      ? user.value.totalFilesLimit
      : null
  })

  const remaining = computed(() =>
    limit.value === null ? null : Math.max(0, limit.value - used.value)
  )

  const showBar = computed(() => tier.value !== 'pro' && limit.value !== null)

  const percentage = computed(() =>
    limit.value ? Math.min(100, (used.value / limit.value) * 100) : 0
  )

  const atLimit = computed(
    () => limit.value !== null && used.value >= limit.value
  )

  const nearLimit = computed(
    () =>
      limit.value !== null && percentage.value >= 80 && percentage.value < 95
  )

  const barColor = computed(() => {
    if (percentage.value === 0) return 'transparent'
    if (percentage.value >= 95) return 'var(--color-red-600)'
    if (percentage.value >= 80) return 'var(--color-yellow-600)'
    return 'var(--color-green-900)'
  })

  const countLabel = computed(() =>
    limit.value === null
      ? formatNumber(used.value)
      : `${formatNumber(used.value)} of ${formatNumber(limit.value)}`
  )

  const cardSubtitle = computed(() =>
    tier.value === 'basic' && !atLimit.value
      ? 'Upgrade your plan to register more work'
      : null
  )

  const banner = computed<MenuBanner | null>(() => {
    if (atLimit.value) {
      return {
        variant: 'error',
        text: "You've reached your registration limit. Upgrade your plan to register more work.",
      }
    }

    if (
      nearLimit.value &&
      tier.value === 'bundle' &&
      remaining.value !== null
    ) {
      return {
        variant: 'info',
        text: `Only ${remaining.value} ${pluralize('registration', remaining.value)} left. Upgrade your plan to keep registering more work.`,
      }
    }

    return null
  })

  const cta = computed<MenuCta | null>(() => {
    if (tier.value === 'basic') {
      return { label: 'Become a Dacr Member', action: 'upgradePro' }
    }

    if (tier.value === 'bundle') {
      return { label: 'Upgrade limit', action: 'upgradeBundle' }
    }

    return null
  })

  const billingFailure = computed(() => {
    const alert = billing.value?.billingAlert
    return (
      (!!alert?.isActive && FAILURE_ALERTS.includes(alert.type)) ||
      !!billing.value?.paymentMethod?.requiresAction
    )
  })

  const formatCents = (cents: number) =>
    formatPrice({
      amount: cents / 100,
      currency: user.value?.upcomingBilling?.currency,
    })

  const overdueCents = computed(
    () => user.value?.upcomingBilling?.outstandingTotal ?? 0
  )

  const registrationCents = computed(
    () => user.value?.upcomingBilling?.registrationTotal ?? 0
  )

  const showOverdueAmount = computed(() => overdueCents.value > 0)

  const showRegistrationAmount = computed(() => registrationCents.value > 0)

  const showAmounts = computed(
    () => showOverdueAmount.value || showRegistrationAmount.value
  )

  const overdueAmount = computed(() => formatCents(overdueCents.value))

  const registrationAmount = computed(() =>
    formatCents(registrationCents.value)
  )

  const registrationSubtitle = computed(() =>
    showRegistrationAmount.value
      ? 'This will be charged to your card momentarily'
      : null
  )

  const billingFailureText = computed(
    () =>
      billing.value?.billingAlert?.description ??
      'We are having a problem billing your account. Please update your payment method or call your payment provider for details on why the transaction failed.'
  )

  return {
    tier,
    used,
    limit,
    showBar,
    percentage,
    barColor,
    countLabel,
    cardSubtitle,
    banner,
    cta,
    atLimit,
    nearLimit,
    billingFailure,
    showAmounts,
    showOverdueAmount,
    showRegistrationAmount,
    overdueAmount,
    registrationAmount,
    registrationSubtitle,
    billingFailureText,
  }
}
